import {axiosInstance} from './http_client';

// Web-push client helper. Uses the raw axios instance (never the cachios
// cache) — subscription state must always be live. All calls assume the
// default Authorization header is set (main.js does this after login).

function isIOS() {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

function isStandalone() {
    return navigator.standalone === true ||
        (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches);
}

// 'supported' | 'ios-needs-install' | 'unsupported'
// On iOS the push objects only exist inside an installed (home screen) PWA,
// and only on 16.4+ — in tab-Safari we show an install hint instead.
export function capability() {
    if (isIOS() && !isStandalone()) return 'ios-needs-install';
    if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
        return 'unsupported';
    }
    return 'supported';
}

export function permission() {
    return ('Notification' in window) ? Notification.permission : 'denied';
}

function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
}

export function getSubscription() {
    if (capability() !== 'supported') return Promise.resolve(null);
    return navigator.serviceWorker.ready.then(reg => reg.pushManager.getSubscription());
}

// Must be called from a click handler — iOS requires a user gesture for
// Notification.requestPermission().
export function subscribe() {
    return axiosInstance.get('app/push/key').then(response => {
        const key = response.data.key;
        if (!key) return Promise.reject(new Error('push not configured'));

        return navigator.serviceWorker.ready.then(reg =>
            reg.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(key)
            })
        ).then(sub =>
            axiosInstance.post('app/push/subscribe', sub.toJSON()).then(() => sub)
        );
    });
}

export function unsubscribe() {
    return getSubscription().then(sub => {
        if (!sub) return null;
        const endpoint = sub.endpoint;
        return sub.unsubscribe().then(() =>
            axiosInstance.post('app/push/unsubscribe', {endpoint})
        );
    });
}

// Idempotent re-sync on app open: re-POSTs the current subscription so the
// server row follows this account (shared devices) and survives pruning. If
// the stored subscription was made against a different VAPID key (rotation),
// resubscribe fresh.
export function sync() {
    if (capability() !== 'supported' || permission() !== 'granted') return;

    getSubscription().then(sub => {
        if (!sub) return;
        axiosInstance.get('app/push/key').then(response => {
            const key = response.data.key;
            if (!key) return;

            const current = sub.options && sub.options.applicationServerKey
                ? btoa(String.fromCharCode.apply(null, new Uint8Array(sub.options.applicationServerKey)))
                    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
                : null;

            if (current && current !== key) {
                sub.unsubscribe().then(() => subscribe()).catch(() => {});
            } else {
                axiosInstance.post('app/push/subscribe', sub.toJSON()).catch(() => {});
            }
        }).catch(() => {});
    }).catch(() => {});
}

export function sendTest() {
    return axiosInstance.post('app/push/test');
}
